import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'react-toastify';

const CartContext = createContext({});

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);

  // Load cart from sessionStorage on mount
  useEffect(() => {
    try {
      const savedCart = sessionStorage.getItem('shopping_cart');
      if (savedCart) {
        setItems(JSON.parse(savedCart));
      }
    } catch (error) {
      console.error('Error loading cart from sessionStorage:', error);
      sessionStorage.removeItem('shopping_cart'); // Clear corrupted data
    }
  }, []);

  // Save cart to sessionStorage whenever items change
  useEffect(() => {
    try {
      sessionStorage.setItem('shopping_cart', JSON.stringify(items));
    } catch (error) {
      console.error('Error saving cart to sessionStorage:', error);
    }
  }, [items]);

  // Calculate totals
  const itemCount = items.reduce((total, item) => total + item.quantity, 0);
  const subtotal = items.reduce((total, item) => total + (item.price * item.quantity), 0);

  // Add item to cart
  const addToCart = (product) => {
    try {
      const resolvedImageUrl =
        product?.imageUrl ||
        product?.images?.[0] ||
        product?.image?.url ||
        '';
      setItems(prevItems => {
        const existingItemIndex = prevItems.findIndex(item => item.id === product.id);

        if (existingItemIndex > -1) {
          // Update quantity of existing item
          const updatedItems = [...prevItems];
          updatedItems[existingItemIndex].quantity += 1;

          toast.success(`Updated ${product.name} quantity`, {
            position: "bottom-right",
            autoClose: 2000,
          });

          return updatedItems;
        } else {
          // Add new item
          toast.success(`${product.name} added to cart`, {
            position: "bottom-right",
            autoClose: 2000,
          });

          return [...prevItems, {
            ...product,
            imageUrl: resolvedImageUrl,
            quantity: 1,
            addedAt: Date.now()
          }];
        }
      });
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add item to cart', {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  // Remove item from cart
  const removeFromCart = (productId) => {
    try {
      setItems(prevItems => {
        const removedItem = prevItems.find(item => item.id === productId);

        if (removedItem) {
          toast.info(`${removedItem.name} removed from cart`, {
            position: "bottom-right",
            autoClose: 2000,
          });
        }

        return prevItems.filter(item => item.id !== productId);
      });
    } catch (error) {
      console.error('Error removing from cart:', error);
      toast.error('Failed to remove item from cart', {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  // Update item quantity
  const updateQuantity = (productId, newQuantity) => {
    try {
      if (newQuantity < 1) {
        removeFromCart(productId);
        return;
      }

      setItems(prevItems => {
        return prevItems.map(item =>
          item.id === productId
            ? { ...item, quantity: newQuantity }
            : item
        );
      });
    } catch (error) {
      console.error('Error updating quantity:', error);
      toast.error('Failed to update quantity', {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  // Clear entire cart
  const clearCart = () => {
    try {
      setItems([]);
      sessionStorage.removeItem('shopping_cart');

      toast.info('Cart cleared', {
        position: "bottom-right",
        autoClose: 2000,
      });
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  };

  const contextValue = {
    items,
    itemCount,
    subtotal,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart
  };

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
